import React, { useEffect, useState } from 'react';
import {
    AppBar,
    Button,
    Container,
    CssBaseline,
    FormControl,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    LinearProgress,
    Paper,
    ThemeProvider,
    StyledEngineProvider,
    Toolbar,
    Typography,
    createTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { JourneyClasses } from '../../../styles/JourneyClasses';
import { FIELD_TYPES } from '../../../utils/utils';
import { Fields } from '../../Fields';
import JHApi from "../../../utils/JHApi"
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SpeedDial, SpeedDialAction } from '@mui/material';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import EmailIcon from '@mui/icons-material/Email';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
    },
    appBar: {
        backgroundColor: '#fbfbfb',
        zIndex: 1
    },
    main: {
        flexGrow: 1,
        height: `calc(100% - 76px)`,
        position: 'absolute',
        right: 0,
        top: 76,
        overflow: 'auto',
        backgroundColor: '#f9fafb',
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(3),
            height: `calc(100% - 124px)`,
            top: 124,
            background: 'linear-gradient(#e8eaff 330px, #fbfbfb 0 100%)'
        }
    },
    logo: {
        width: 150,
        marginBottom: 7,
        [theme.breakpoints.up('sm')]: {
            width: 250,
        }
    },
    headerPlace: {
        height: 76,
        [theme.breakpoints.up('sm')]: {
            height: 124,
        }
    },
    headerContainer: {
        [theme.breakpoints.down('sm')]: {
            padding: '8px 15px',
        },
        [theme.breakpoints.up('sm')]: {
            paddingTop: 22,
        }
    },
    WASupport: {
        position:'fixed', 
        right: 10, 
        bottom:30, 
        zIndex:999, 
        textDecoration:'none', 
        direction: 'ltr',
        [theme.breakpoints.down('sm')]: {
            right: -55,
            bottom:5, 
        },
    },
    WASpan: {
        width: 50, 
        height: 50, 
        backgroundColor:'white', 
        display:'inline-block', 
        borderRadius:'50%'
    },
    WASupportIcon: {
        color: '#25d366', 
        fontSize: 60, 
        fontWeight: 'bold', 
        position: 'relative', 
        right: 55, 
        top: 5,
    },
    speedDial: {
        position: 'fixed',
        bottom: 16,
        right: 16,
    },
}));

const localTheme = createTheme({
    typography: {
        fontFamily: [
            "almoni-neue-aaa",
            'DM Sans',
            'sans-serif'
        ].join(','),
    },
    components: {
        MuiGrid: {
            styleOverrides: {
              root: {
                // Removes the global negative margin bug on all grid containers
                marginTop: '0px !important',
              },
            },
        },
        MuiCssBaseline: {
            styleOverrides: `
            @font-face {
                font-family: 'DM Sans';
                font-style: italic;
                font-weight: 100 1000;
                font-display: swap;
                src: url(https://fonts.gstatic.com/s/dmsans/v17/rP2Fp2ywxg089UriCZa4ET-DNl0.woff2) format('woff2');
                unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
            }
            `,
        },
    },
    palette: {
        primary: {
            main: '#6366f1',
        },
        secondary: {
            main: '#fbbc04'
        }
    }
});

const WAButton = withStyles((theme) => ({
    root: {
      color: 'white',
      backgroundColor: '#22c55e',
      '&:hover': {
        backgroundColor: '#16a34a',
      },
    },
}))(Button);

export default function AfterPayment(props) {
    /*const afterPaymentData = props.afterPaymentData || {}
    const afterPaymentLoading = props.afterPaymentLoading || false*/
    const token = props.token
    const mainError = props.error

    const [afterPaymentData, setAfterPaymentData] = useState({});
    const [error, setError] = useState();
    const [newData, setNewData] = useState({});
    const [disabled, setDisabled] = useState(false);
    const [step, setStep] = useState('Check Data');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showSupport, setShowSupport] = useState(false);

    useEffect(() => {
        if (mainError || !token || afterPaymentData.userCreated) return

        let intervalId = setInterval(() => {
            setDisabled(true)
            setError(false)
            JHApi.getAfterPaymentData(token,(response)=>{
                setAfterPaymentData(response)
                setDisabled(false)

                if (response.userCreated === true) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }, (error) => {
                if(error && error.response&& error.response.data&& error.response.data.error){
                    setError(error.response.data.error)   
                } else {
                    setError('Something went wrong')
                }
                clearInterval(intervalId);
                intervalId = null;
                setDisabled(false)
            })
        }, 1000);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [token, mainError, afterPaymentData]);

    const getValue = (name, defaultValue) => {
        if (newData[name] !== undefined){
            return newData[name]
        } else if (afterPaymentData[name] !== undefined){
            return afterPaymentData[name]
        } else {
            return defaultValue
        }
    }

    const handleChangeField = e => {
        setNewData({...newData,[e.target.name]: e.target.value})
    };

    const handleSaveFields = e => {
        e.preventDefault();
    
        setError()
        setDisabled(true)

        JHApi.updateAfterPaymentData(afterPaymentData.token, {newData},(response)=>{
            setNewData({
                password: '',
                confirmPassword:''
            })
            setStep('Choose Password')
            setDisabled(false)
        }, (error) => {
            setError(error)
            setDisabled(false)
        })
    }

    const handleChoosePassword = e => {
        e.preventDefault();

        if ((newData.password === newData.confirmPassword)){
            setError()
            setDisabled(true)

            JHApi.updatePasswordAfterPayment(afterPaymentData.token, {password:newData.password},(response)=>{
                if (response){
                    props.loginAfterPayment(response)
                } else {
                    setDisabled(false)
                }
            }, (error) => {
                setError(error)
                setDisabled(false)
            })
        }
    }

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword)
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const fields = [
        {name:'email', label:'Email', inputType:'email', defaultValue: '', type:FIELD_TYPES.TEXT, sm:12, required: true},
        {name:'phone', label:'Phone', inputType:'tel', defaultValue: '', type:FIELD_TYPES.TEXT, sm:12, required: true},
        {name:'first_name', label:'First name', defaultValue: '', type:FIELD_TYPES.TEXT, sm:12, required: true},
        {name:'last_name', label:'Last name', defaultValue: '', type:FIELD_TYPES.TEXT, sm:12, required: true},
    ]

    const classes = useStyles();
    const journeyClasses = JourneyClasses();

    return (
        <StyledEngineProvider injectFirst>(<ThemeProvider theme={localTheme}>
                    <CssBaseline />
                    <AppBar position="fixed" className={classes.appBar}>
                        <Container className={classes.headerContainer}>
                            <Toolbar style={{padding:0}}>
                                <img className={classes.logo} src="https://jobhunt.co.il/wp-content/themes/jobboard/assets/images/jobhunt-logo.svg" alt="סוכן טאלנטים אישי לעולם ההיי-טק" />
                                <Typography style={{color: '#aa4dc8', marginLeft: 8, maxWidth: 120, marginTop: 10, padding: '4px 23px', letterSpacing: '1px', fontWeight: 700}}>התחברות</Typography>
                            </Toolbar>
                        </Container>
                    </AppBar>
                    <div className={classes.headerPlace}></div>
                    <main className={classes.main}>
                        <Container>
                            {afterPaymentData.userCreated?<>
                                <div style={{marginBottom: 8, textAlign: 'center'}}>
                                    <div>
                                        <CheckCircleOutlineIcon style={{color: '#0ec42e', fontSize: 54, marginTop: 24}}/>
                                    </div>
                                    <Typography style={{fontWeight: 800, color: '#aa4dc8', fontSize:50}} variant='h3'>Payment Completed!</Typography>
                                    <Typography style={{fontWeight: 800, marginTop: 8, marginBottom: 24, fontSize: 36}} variant='h4'>Your JobHunt start now</Typography>
                                </div>

                                {(step === 'Check Data')&&<Paper className={journeyClasses.block} elevation={1} style={{maxWidth: 350, margin: '0 auto'}}>
                                    <form onSubmit={handleSaveFields}>
                                        <Typography style={{fontSize:22}} className={journeyClasses.stepTtile} variant='h3'>
                                            Check your data
                                        </Typography>
                                        <Typography style={{fontSize:18}} className={journeyClasses.stepText}>
                                            This data will be used for job search
                                        </Typography>
                                        {(error)&&<Typography style={{fontSize:18}} color="error">{error}</Typography>}
                                        <Fields fields={fields} getValue={getValue} handleChange={handleChangeField} fontSize={18}/>
                                        <Button style={{marginTop: 8, fontSize: 18}} className={journeyClasses.stepButton} disabled={disabled} color='primary' variant='contained' type="submit">I will use this data for job search</Button>
                                    </form>
                                </Paper>}

                                {(step === 'Choose Password')&&<Paper className={journeyClasses.block} elevation={1} style={{maxWidth: 350, margin: '0 auto'}}>
                                    <form onSubmit={handleChoosePassword}>
                                        <Typography style={{fontSize:22}} className={journeyClasses.stepTtile} variant='h3'>
                                            Choose your account password
                                        </Typography>
                                        {(error)&&<Typography style={{fontSize:18}} color="error">{error}</Typography>}
                                        <FormControl variant="standard" margin="normal" required fullWidth>
                                            <InputLabel style={{fontSize:18}} htmlFor="password">Password</InputLabel>
                                            <Input
                                                style={{fontSize:18}}
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                value={newData.password}
                                                onChange={handleChangeField}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="Toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            size="large">
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>
                                        <FormControl variant="standard" margin="normal" required fullWidth>
                                            <InputLabel style={{fontSize:18}} htmlFor="confirmPassword" error={(newData.password !== newData.confirmPassword)}>Confirm password</InputLabel>
                                            <Input
                                                style={{fontSize:18}}
                                                name="confirmPassword"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                value={newData.confirmPassword}
                                                onChange={handleChangeField}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="Toggle password visibility"
                                                            onClick={handleClickShowConfirmPassword}
                                                            size="large">
                                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>
                                        <Button style={{fontSize:18}} className={journeyClasses.stepButton} disabled={disabled} color='primary' variant='contained' type="submit">Save</Button>
                                    </form>
                                </Paper>}
                            </>:<Paper style={{textAlign: 'center',width: '100%',margin: '8px auto',maxWidth: 400}} className={journeyClasses.block} elevation={1}>
                                {(mainError || error)?<>
                                    <Typography className={journeyClasses.stepTtile} variant='h3' color="error">{mainError || error}, Please contact us</Typography>
                                    <WAButton href={window.OFFICE_LINK} style={{marginBottom:8}} className={journeyClasses.stepButton} variant="contained" color="primary" aria-label="Contact us on WhatsApp" size="small">
                                        <WhatsAppIcon style={{marginRight: 8}}/>    
                                        Contact us on WhatsApp
                                    </WAButton>
                                    <Button href='mailto:aneli@jobhunt.co.il,office@jobhunt.co.il' className={journeyClasses.stepButton} variant="contained" color="primary" aria-label="Contact us on Email" size="small">
                                        <EmailIcon style={{marginRight: 8}}/>    
                                        Contact us on Email
                                    </Button>
                                </>:<>
                                    <Typography className={journeyClasses.stepTtile} variant='h3'>
                                        Creating Account
                                    </Typography>
                                    <LinearProgress />
                                </>}
                            </Paper>}
                        </Container>
                    </main>
                    <SpeedDial
                        ariaLabel="Support"
                        className={classes.speedDial}
                        //hidden={hidden}
                        icon={<ContactSupportIcon />}
                        onClose={()=>setShowSupport(false)}
                        onOpen={()=>setShowSupport(true)}
                        open={showSupport}
                        direction='up'
                    >
                        <SpeedDialAction
                            href={window.OFFICE_LINK}
                            target="_blank" rel="noopener noreferrer" 
                            icon={<WhatsAppIcon />}
                            tooltipTitle='WhatsApp'
                        />
                        <SpeedDialAction
                            href="mailto:aneli@jobhunt.co.il,office@jobhunt.co.il"
                            target="_blank" rel="noopener noreferrer" 
                            icon={<EmailIcon />}
                            tooltipTitle='Email'
                        />
                    </SpeedDial>
                </ThemeProvider>)
                    </StyledEngineProvider>
    );
}